// src/domains/auth.ts —— H5 认证与引导（原生）：06 U-01 登录二段式 / 注册条款 / TG 登录与绑定，U-16b 找回密码三步
import { ApiError } from '../../../shared/src/api/client';
import type { RegisterReq, RegisterSendCodeReq, TokenResp } from '../../../shared/src/api/types';
import type { Dict } from '../../../shared/src/dc';
import { getTg } from '../../../shared/src/tg';
import { I18N_AUTH, REWRITES_AUTH } from '../i18n/auth';
import { KEYS, MODAL_KEYS } from './auth.keys';
import { legalParts } from './legal';
import { botIdField, str, type ClickEvent, type H5Ctx, type H5Domain, type InputEvent } from './types';

export interface ResetSlice {
  step: 1 | 2 | 3;
  via: 'email' | 'phone';
  target: string;
  code: string;
  nw: string;
  nw2: string;
  err: string;
  cd: number;
  token: string; // reset_token：仅内存（U-16b），关弹层 / 回 step1 / 成功即丢弃
}

export interface AuthSlice {
  mode: 'login' | 'register';
  email: string;
  pwd: string;
  code: string;
  invite: string;
  terms: boolean;
  showPwd: boolean;
  need2fa: boolean;
  totp: string;
  err: string;
  tip: string;
  codeCd: number;
  bindTg: boolean;
  rs: ResetSlice;
}

declare module '../state' {
  interface H5Slices {
    a: AuthSlice;
  }
}

const EMAIL_RE = /^[^@\s]+@[^@\s]+\.[a-z]{2,}$/i; // 原型 L792 / 06 U-01
const PWD_RE = /^(?=.*[A-Za-z])(?=.*\d).{8,64}$/; // X-24 / 07 §3.3
const CODE_LEN = 6;
const CD_SECONDS = 60; // 原型 L681 / L573

// 模板直接展示的本域新增文案键（经 authT 下发；build split.test 双语守卫要求模板 t.* 只引用原型字典键）
export const AUTH_TEXT_KEYS = ['pwdRule', 'totp', 'tgBindEmail', 'inviteFromLink'] as const;

const RS0: ResetSlice = { step: 1, via: 'email', target: '', code: '', nw: '', nw2: '', err: '', cd: 0, token: '' };
export const A0: AuthSlice = {
  mode: 'login', email: '', pwd: '', code: '', invite: '', terms: false, showPwd: false, need2fa: false, totp: '', err: '', tip: '', codeCd: 0, bindTg: false, rs: RS0,
};

// 倒计时按宿主实例隔离（多实例 / 多用例不串计时器）
interface Timers {
  code: ReturnType<typeof setInterval> | null;
  rs: ReturnType<typeof setInterval> | null;
}
const timersOf = new WeakMap<object, Timers>();

function timers(ctx: H5Ctx): Timers {
  let t = timersOf.get(ctx.host);
  if (!t) {
    t = { code: null, rs: null };
    timersOf.set(ctx.host, t);
  }
  return t;
}

function stopTimer(ctx: H5Ctx, which: keyof Timers): void {
  const t = timers(ctx);
  const h = t[which];
  if (h !== null) clearInterval(h);
  t[which] = null;
}

// 处理器一律读宿主当前 state 与当前语言字典：renderVals 快照在异步 / 定时回调中已过期
function cur(ctx: H5Ctx): AuthSlice {
  return ctx.host.state.a;
}

function dict(ctx: H5Ctx): Dict {
  return ctx.gp.ui.dict();
}

function setA(ctx: H5Ctx, patch: Partial<AuthSlice>): void {
  ctx.host.setState({ a: { ...cur(ctx), ...patch } });
}

function setRs(ctx: H5Ctx, patch: Partial<ResetSlice>): void {
  const a = cur(ctx);
  ctx.host.setState({ a: { ...a, rs: { ...a.rs, ...patch } } });
}

function countdown(ctx: H5Ctx, which: keyof Timers): void {
  stopTimer(ctx, which);
  if (which === 'code') setA(ctx, { codeCd: CD_SECONDS });
  else setRs(ctx, { cd: CD_SECONDS });
  timers(ctx)[which] = setInterval(() => {
    const a = cur(ctx);
    const n = Math.max(0, (which === 'code' ? a.codeCd : a.rs.cd) - 1);
    if (which === 'code') setA(ctx, { codeCd: n });
    else setRs(ctx, { cd: n });
    if (n <= 0) stopTimer(ctx, which);
  }, 1000);
}

function registerOpen(ctx: H5Ctx): boolean {
  return ctx.s.sh.config?.switches.register !== false;
}

// 归属修复（2026-10-21）U-01：经推广链接带入（sh.ref）且服务端确认有效（sh.refValid ← config?ref= 的 ref_valid，与注册归属同口径）→ 邀请码锁定只读，
// 防止用户误删 / 改码导致代理丢归属；无效码按现状视同无码、保持可编辑（用户手填场景不受影响）。读宿主当前 state（处理器内快照可能过期）。
export function inviteLocked(ctx: H5Ctx): boolean {
  const sh = ctx.host.state.sh;
  return sh.ref !== '' && sh.refValid;
}

// 提交用邀请码：锁定时恒为链接码（不信任输入框内容），否则为用户输入（trim + 大写）
function inviteOf(ctx: H5Ctx): string {
  return (inviteLocked(ctx) ? ctx.host.state.sh.ref : cur(ctx).invite).trim().toUpperCase();
}

function retryText(t: Dict, e: unknown): string | null {
  return e instanceof ApiError && e.retryAfter !== null ? `${e.retryAfter} ${str(t, 'resendIn')}` : null;
}

async function afterAuth(ctx: H5Ctx, token: string, opts: { register: boolean; lang: 'zh' | 'en'; bindTg: boolean }): Promise<void> {
  const g = ctx.gp;
  const t = dict(ctx);
  // TD-811 / S-61：Mini App 形态（带 initData）任何登录方式都只签 12h access，token 仅内存
  await g.session.established(token, { persist: !ctx.host.state.sh.miniApp, toast: opts.bindTg ? undefined : str(t, 'toastLogin'), rollbackOnFail: true });
  const invite = cur(ctx).invite;
  stopTimer(ctx, 'code');
  ctx.host.setState({ a: { ...A0, invite } }); // 凭据与动态码不在内存滞留
  // U-19：注册成功后以当前界面语言写回 profile（乐观更新，失败重试 1 次，见 shell.setLang）
  if (opts.register) g.session.setLang(opts.lang);
  if (!opts.bindTg) return;
  const tg = getTg();
  // 绑定须 Mini App initData（S-24）；入口仅在 sh.miniApp 时显示，此处兜底拦截空 initData，不发请求
  if (!tg?.initData) {
    g.ui.toast(str(dict(ctx), 'errTgAuth'), 'error');
    return;
  }
  const initData = tg.initData;
  try {
    // S-24：绑定属身份变更，已开 2FA 须取新的动态码（登录刚用过的码同窗口不可复用 → totpWaitNext）
    const r = await g.totp.run(
      (totp) => g.api.call('POST /api/user/telegram/bind', { body: { init_data: initData, ...botIdField(ctx.host.state.sh.botId), ...(totp ? { totp } : {}) } }),
      { waitNext: true },
    );
    if (r === null) return;
    g.ui.toast(str(dict(ctx), 'tgBoundOk'), 'success');
    await g.session.reloadProfile().catch((e: unknown) => console.warn('[gp:h5] 绑定 TG 后刷新 profile 失败', e));
  } catch (e) {
    // 1008 / 1009：toast 提示，保持已登录的邮箱账号（06 U-01）
    g.ui.toast(g.ui.err(e), 'error');
  }
}

function validate(a: AuthSlice, t: Dict): Partial<AuthSlice> | null {
  const email = a.email.trim();
  if (a.mode === 'register') {
    if (!email || a.code.length !== CODE_LEN || !a.pwd) return { err: str(t, 'errRegFill'), tip: '' };
    if (!EMAIL_RE.test(email)) return { err: str(t, 'errEmail'), tip: '' };
    if (!PWD_RE.test(a.pwd)) return { err: str(t, 'errPwdRule'), tip: '' };
    if (!a.terms) return { err: str(t, 'errTerms'), tip: '' };
    return null;
  }
  if (!email || !a.pwd) return { err: str(t, 'errLogin'), tip: '' };
  if (!EMAIL_RE.test(email)) return { err: str(t, 'errEmail'), tip: '' };
  if (a.need2fa && a.totp.length !== CODE_LEN) return { err: str(t, 'err2fa'), tip: '', totp: '' };
  return null;
}

async function submit(ctx: H5Ctx): Promise<void> {
  const a = cur(ctx);
  const t = dict(ctx);
  const bad = validate(a, t);
  if (bad) return setA(ctx, bad);
  const email = a.email.trim();
  const lang = ctx.host.state.lang;
  await ctx.gp.ui.run('login', async () => {
    try {
      if (a.mode === 'register') {
        const invite = inviteOf(ctx);
        // 当前界面语言（登录页 = localStorage.gpu_lang）随注册提交：后端据此写 users.lang。
        // bot_id（归属修复 2026-10-21）：代理 Bot 的 Mini App 内无码邮箱注册由服务端按该 Bot 归属代理回落（邀请码有效时以码为准）
        const body: RegisterReq = {
          email, code: a.code, password: a.pwd, terms_accepted: true, lang,
          ...(invite ? { invite_code: invite } : {}), ...botIdField(ctx.host.state.sh.botId),
        };
        const r = await ctx.gp.api.call<TokenResp>('POST /api/public/register', { body });
        await afterAuth(ctx, r.token, { register: true, lang, bindTg: false });
        return;
      }
      const r = await ctx.gp.api.call<TokenResp>('POST /api/public/login', { body: { email, password: a.pwd, ...(a.need2fa ? { totp: a.totp } : {}) } });
      await afterAuth(ctx, r.token, { register: false, lang, bindTg: a.bindTg });
    } catch (e) {
      // D17：1004 need_2fa 为二段式提示（非失败），展开 TOTP 行
      if (a.mode === 'login' && e instanceof ApiError && e.code === 1004 && e.data.need_2fa) {
        setA(ctx, { need2fa: true, totp: '', err: '', tip: str(dict(ctx), 'need2faTip') });
        return;
      }
      setA(ctx, { err: ctx.gp.ui.err(e, {}, a.mode === 'register' ? 'register' : undefined), tip: '', totp: '' });
    }
  });
}

async function sendCode(ctx: H5Ctx): Promise<void> {
  const a = cur(ctx);
  const t = dict(ctx);
  if (a.codeCd > 0) return;
  const email = a.email.trim();
  if (!EMAIL_RE.test(email)) return setA(ctx, { err: str(t, 'errEmail'), tip: '' });
  // 发码在途互斥（U-20 busy）：倒计时在应答后才起，连点不得重复发信
  await ctx.gp.ui.run('code', async () => {
    try {
      // 注册码邮件 M-01 按当前界面语言发送（登录页 = localStorage.gpu_lang）
      const body: RegisterSendCodeReq = { email, lang: ctx.host.state.lang };
      await ctx.gp.api.call('POST /api/public/register/send-code', { body });
      setA(ctx, { err: '' });
      ctx.gp.ui.toast(str(dict(ctx), 'toastCode'), 'success');
      countdown(ctx, 'code');
    } catch (e) {
      setA(ctx, { err: retryText(dict(ctx), e) ?? ctx.gp.ui.err(e, {}, 'register'), tip: '' });
    }
  });
}

async function tgLogin(ctx: H5Ctx): Promise<void> {
  const tg = getTg();
  if (!tg?.initData) {
    // 06 U-01：非 TG 环境跳 Bot 深链（原型此按钮复用 doLogin，矛盾 #2）
    // 有效推广码随 start 带给 Bot（任一 Bot 带 start=码 均按码归属，07 §3.2 步骤 5），Bot /start 据此固化归属
    const u = ctx.host.state.sh.config?.bot?.username;
    const start = inviteLocked(ctx) ? `?start=${encodeURIComponent(ctx.host.state.sh.ref)}` : '';
    if (u) window.location.href = `https://t.me/${u.replace(/^@/, '')}${start}`;
    return;
  }
  await ctx.gp.ui.run('login', async () => {
    try {
      const r = await ctx.gp.api.call<TokenResp>('POST /api/public/login/telegram', { body: { init_data: tg.initData, ...botIdField(ctx.host.state.sh.botId) } });
      await ctx.gp.session.established(r.token, { persist: false, toast: str(dict(ctx), 'toastLogin'), termsBanner: true, rollbackOnFail: true });
    } catch (e) {
      setA(ctx, { err: str(dict(ctx), e instanceof ApiError && e.code === 1503 ? 'regClosed' : 'errTgAuth'), tip: '' });
    }
  });
}

async function sendResetCode(ctx: H5Ctx, email: string): Promise<boolean> {
  try {
    await ctx.gp.api.call('POST /api/public/password-reset/send-code', { body: { email } });
    countdown(ctx, 'rs');
    ctx.gp.ui.toast(str(dict(ctx), 'toastCode'), 'success');
    return true;
  } catch (e) {
    setRs(ctx, { err: retryText(dict(ctx), e) ?? ctx.gp.ui.err(e) });
    return false;
  }
}

async function rsNext(ctx: H5Ctx): Promise<void> {
  const r = cur(ctx).rs;
  const t = dict(ctx);
  const api = ctx.gp.api;
  const email = r.target.trim();
  if (r.step === 1) {
    // 06 U-16b：一期仅邮箱通道，手机通道前端直接 errPhoneClosed，不发请求
    if (r.via === 'phone') return setRs(ctx, { err: str(t, 'errPhoneClosed') });
    if (!EMAIL_RE.test(email)) return setRs(ctx, { err: str(t, 'errTarget') });
    await ctx.gp.ui.run('rs', async () => {
      if (await sendResetCode(ctx, email)) setRs(ctx, { step: 2, err: '', code: '' });
    });
    return;
  }
  if (r.step === 2) {
    if (r.code.length !== CODE_LEN) return setRs(ctx, { err: str(t, 'errCode') });
    // step3 退回 step2 且验证码未改：沿用已签发的 reset_token，不重复 verify（onRsCode 改码即清空）
    if (r.token) return setRs(ctx, { step: 3, err: '' });
    await ctx.gp.ui.run('rs', async () => {
      try {
        const v = await api.call<{ reset_token: string }>('POST /api/public/password-reset/verify', { body: { email, code: r.code } });
        setRs(ctx, { step: 3, token: v.reset_token, err: '', nw: '', nw2: '' });
      } catch (e) {
        setRs(ctx, { err: ctx.gp.ui.err(e), code: '' });
      }
    });
    return;
  }
  if (!PWD_RE.test(r.nw) || r.nw !== r.nw2) return setRs(ctx, { err: str(t, 'errNewPwd') });
  await ctx.gp.ui.run('rs', async () => {
    try {
      await api.call('POST /api/public/password-reset/commit', { body: { reset_token: r.token, new_password: r.nw } });
      stopTimer(ctx, 'rs');
      ctx.host.setState({ modal: null, a: { ...cur(ctx), pwd: '', rs: RS0 } });
      ctx.gp.ui.toast(str(dict(ctx), 'resetDone'), 'success');
    } catch (e) {
      // reset_token 失效（1003）/ 未验证票据（1012）→ 回 step1 并行内 errCode
      if (e instanceof ApiError && (e.code === 1003 || e.code === 1012)) {
        stopTimer(ctx, 'rs');
        setRs(ctx, { ...RS0, target: r.target, via: r.via, err: str(dict(ctx), 'errCode') });
        return;
      }
      setRs(ctx, { err: ctx.gp.ui.err(e) });
    }
  });
}

// 原型 L676–677；注册关闭态 G-08：文字 #b6bfba、cursor:not-allowed、永不呈选中态
function tabStyle(active: boolean, disabled: boolean): string {
  if (disabled) return 'flex:1;height:38px;border:0;border-radius:9px;font-weight:700;font-size:14px;cursor:not-allowed;background:transparent;color:#b6bfba;box-shadow:none';
  return `flex:1;height:38px;border:0;border-radius:9px;font-weight:700;font-size:14px;cursor:pointer;background:${active ? '#fff' : 'transparent'};color:${active ? '#0f1a17' : '#6b7772'};box-shadow:${active ? '0 1px 3px rgba(0,0,0,.08)' : 'none'}`;
}

function digits(v: string): string {
  return v.replace(/\D/g, '').slice(0, CODE_LEN);
}

function resetAll(ctx: H5Ctx): void {
  stopTimer(ctx, 'code');
  stopTimer(ctx, 'rs');
  ctx.host.setState({ a: { ...A0, invite: cur(ctx).invite } });
}

export const auth: H5Domain = {
  name: 'auth',
  keys: KEYS,
  modalKeys: MODAL_KEYS,
  migrated: true,
  i18n: I18N_AUTH,
  rewrites: REWRITES_AUTH,
  state: () => ({ a: A0 }),
  mount(ctx) {
    ctx.events.on('session:lost', ({ reason }) => {
      const t = ctx.gp.ui.dict();
      if (reason === 'tg_register_closed') setA(ctx, { err: str(t, 'regClosed'), tip: '' });
      else if (reason === 'tg_auth_failed') setA(ctx, { err: str(t, 'errTgAuth'), tip: '' });
      else if (reason === 'logout' || reason === 'expired') resetAll(ctx);
    });
  },
  update(ctx, prev) {
    // P-22：config 到达后按 ref 预填邀请码并决定默认模式（register=false 时保持登录且不预填）
    if (!prev.sh.config && ctx.s.sh.config && ctx.s.sh.ref && ctx.s.sh.config.switches.register !== false) {
      setA(ctx, { invite: ctx.s.sh.ref.toUpperCase(), mode: 'register' });
    }
    // U-16b：找回弹层以任何方式关闭（蒙层 / BackButton / 取消）均停倒计时并丢弃 reset_token
    if (prev.modal?.type === 'reset' && ctx.s.modal?.type !== 'reset') {
      stopTimer(ctx, 'rs');
      if (ctx.s.a.rs !== RS0) setA(ctx, { rs: RS0 });
    }
  },
  unmount(ctx) {
    stopTimer(ctx, 'code');
    stopTimer(ctx, 'rs');
  },
  vals(ctx) {
    const { s, t, lang } = ctx;
    const a = s.a;
    const rs = a.rs;
    const regOpen = registerOpen(ctx);
    const site = s.sh.config?.site_url ?? '';
    return {
      authT: Object.fromEntries(AUTH_TEXT_KEYS.map((k) => [k, str(t, k)])),
      isLogin: s.screen === 'login',
      isRegisterMode: a.mode === 'register',
      loginEmail: a.email,
      onLoginEmail: (e: InputEvent) => setA(ctx, { email: e.target.value, need2fa: false, totp: '', tip: '' }),
      loginPwd: a.pwd,
      onLoginPwd: (e: InputEvent) => setA(ctx, { pwd: e.target.value }),
      loginError: a.err,
      inviteCode: a.invite,
      onInvite: (e: InputEvent) => {
        if (inviteLocked(ctx)) return; // 锁定态不接受改写（模板已渲染为只读展示，此处兜底）
        setA(ctx, { invite: e.target.value.toUpperCase() });
      },
      inviteLocked: inviteLocked(ctx),
      inviteEditable: !inviteLocked(ctx),
      inviteLockedText: s.sh.ref.toUpperCase(),
      pwdType: a.showPwd ? 'text' : 'password',
      showPwd: a.showPwd,
      hidePwd: !a.showPwd,
      togglePwd: () => setA(ctx, { showPwd: !cur(ctx).showPwd }),
      loginTabStyle: tabStyle(a.mode === 'login', false),
      registerTabStyle: tabStyle(a.mode === 'register' && regOpen, !regOpen),
      primaryAuthText: a.mode === 'login' ? str(t, a.bindTg ? 'loginAndBind' : 'login') : str(t, 'register'),
      codeBtnText: a.codeCd > 0 ? `${a.codeCd}s` : str(t, 'getCode'),
      setModeLogin: () => setA(ctx, { mode: 'login', err: '', tip: '', need2fa: false, totp: '' }),
      setModeRegister: () => {
        if (!registerOpen(ctx)) return ctx.gp.ui.toast(str(dict(ctx), 'regClosed'), 'error');
        setA(ctx, { mode: 'register', err: '', tip: '', need2fa: false, totp: '', bindTg: false });
      },
      sendCode: () => void sendCode(ctx),
      doLogin: () => void submit(ctx),
      busyLogin: s.busy === 'login',
      zhBtnStyle: `border:0;background:transparent;cursor:pointer;font-weight:700;font-size:13px;color:${lang === 'zh' ? '#0d9a68' : '#9aa5a1'}`,
      enBtnStyle: `border:0;background:transparent;cursor:pointer;font-weight:700;font-size:13px;color:${lang === 'en' ? '#0d9a68' : '#9aa5a1'}`,
      inTelegram: s.sh.inTelegram,
      regCode: a.code,
      onRegCode: (e: InputEvent) => setA(ctx, { code: digits(e.target.value) }),
      need2fa: a.need2fa,
      totpLogin: a.totp,
      onTotpLogin: (e: InputEvent) => {
        const v = digits(e.target.value);
        setA(ctx, { totp: v, err: '' });
        // D17：输满 6 位自动提交（submit 读宿主当前 state，busy 互斥防重复）
        if (v.length === CODE_LEN) void submit(ctx);
      },
      loginTip: a.err ? '' : a.tip,
      termsBoxStyle: `flex:none;width:18px;height:18px;border-radius:5px;display:inline-flex;align-items:center;justify-content:center;font-size:12px;font-weight:800;margin-top:1px;border:${a.terms ? '0' : '1px solid #dfe5e2'};background:${a.terms ? '#0d9a68' : '#fff'};color:${a.terms ? '#fff' : 'transparent'}`,
      toggleTerms: () => setA(ctx, { terms: !cur(ctx).terms, err: '' }),
      termsParts: legalParts(str(t, 'termsAgree'), site, lang),
      showLoginHint: a.mode === 'login',
      loginHintParts: legalParts(str(t, 'loginHint'), site, lang),
      tgLogin: () => void tgLogin(ctx),
      // 「用邮箱登录并绑定」须有 Mini App initData（绑定请求体 init_data）；仅 inTelegram 而无 initData（如 TG 内打开普通链接）不提供
      showTgBindEmail: s.sh.miniApp && !a.bindTg,
      startBindTg: () => {
        if (!ctx.host.state.sh.miniApp) return;
        setA(ctx, { bindTg: true, mode: 'login', err: '', tip: '', need2fa: false, totp: '' });
      },
      openReset: (e: ClickEvent) => {
        e.preventDefault();
        stopTimer(ctx, 'rs');
        const now = cur(ctx);
        ctx.host.setState({ modal: { type: 'reset', title: str(dict(ctx), 'resetTitle') }, a: { ...now, rs: { ...RS0, target: now.email.trim() } } });
      },
      rs: { target: rs.target, code: rs.code, nw: rs.nw, nw2: rs.nw2, err: rs.err },
      rsIs1: rs.step === 1,
      rsIs2: rs.step === 2,
      rsIs3: rs.step === 3,
      // 原型 L777–779 逐字
      rsSteps: [str(t, 'resetStep1'), str(t, 'resetStep2'), str(t, 'resetStep3')].map((l, i) => ({
        label: l,
        n: i + 1,
        style: `flex:1;display:flex;flex-direction:column;align-items:center;gap:6px;font-size:11px;font-weight:700;color:${rs.step >= i + 1 ? '#0d9a68' : '#b6bfba'}`,
        dot: `width:26px;height:26px;border-radius:50%;display:inline-flex;align-items:center;justify-content:center;font-size:12px;font-weight:800;background:${rs.step >= i + 1 ? '#0d9a68' : '#e6ebe8'};color:${rs.step >= i + 1 ? '#fff' : '#8a958f'}`,
      })),
      // 原型 L780–781 逐字
      rsVias: ([['email', str(t, 'byEmail')], ['phone', str(t, 'byPhone')]] as const).map(([k, l]) => ({
        label: l,
        pick: () => setRs(ctx, { via: k, target: '', err: '' }),
        style: `flex:1;height:42px;border:1px solid ${rs.via === k ? '#0d9a68' : '#dfe5e2'};border-radius:12px;background:${rs.via === k ? '#e3f6ee' : '#fff'};color:${rs.via === k ? '#0d9a68' : '#0f1a17'};font-weight:700;font-size:13px;cursor:pointer`,
      })),
      rsLabel: rs.via === 'email' ? str(t, 'email') : str(t, 'phone'),
      rsPh: rs.via === 'email' ? 'name@example.com' : str(t, 'phonePh'),
      rsTargetMask: rs.target.trim().replace(/^(.{2}).*(.{3})$/, '$1***$2'), // 原型 L784
      onRsTarget: (e: InputEvent) => setRs(ctx, { target: e.target.value, err: '' }),
      onRsCode: (e: InputEvent) => setRs(ctx, { code: digits(e.target.value), err: '', token: '' }),
      onRsNw: (e: InputEvent) => setRs(ctx, { nw: e.target.value, err: '' }),
      onRsNw2: (e: InputEvent) => setRs(ctx, { nw2: e.target.value, err: '' }),
      rsCdText: rs.cd > 0 ? `${rs.cd} ${str(t, 'resendIn')}` : str(t, 'resend'),
      rsResend: () => {
        const r = cur(ctx).rs;
        if (r.cd > 0 || r.step !== 2) return;
        void ctx.gp.ui.run('rs', () => sendResetCode(ctx, r.target.trim())); // 重发在途互斥
      },
      rsNext: () => void rsNext(ctx),
      rsPrevLabel: rs.step === 1 ? str(t, 'cancel') : str(t, 'prev'),
      rsPrev: () => {
        const r = cur(ctx).rs;
        if (r.step === 1) {
          ctx.gp.ui.closeModal(); // update() 统一停倒计时、丢弃 reset_token
          return;
        }
        // 返回 step1 时丢弃 reset_token（06 U-16b）
        setRs(ctx, r.step === 2 ? { step: 1, err: '', token: '', code: '' } : { step: 2, err: '', nw: '', nw2: '' });
      },
      busyRs: s.busy === 'rs',
    };
  },
  modal: (ctx) => ({ isReset: ctx.s.modal?.type === 'reset' }),
};
